'use client';
import { useDesign } from '@/context/DesignContext';

interface OpeningHoursProps {
  hours?: any; // can be flat or nested
  title?: string;
  design?: any;
  style?: React.CSSProperties;
}

const defaultHours = {
  monday: '09:00 - 18:00',
  tuesday: '09:00 - 18:00',
  wednesday: '09:00 - 18:00',
  thursday: '09:00 - 18:00',
  friday: '09:00 - 18:00',
  saturday: '10:00 - 16:00',
  sunday: 'Fermé',
};

const dayLabels: Record<string, string> = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche',
  holidays: 'Jours fériés',
  breakfast: 'Petit-déjeuner',
  lunchDinner: 'Déjeuner / Dîner',
};

export default function OpeningHours({
  hours = defaultHours,
  title = 'Horaires d\'ouverture',
  design: propDesign,
  style,
}: OpeningHoursProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  const bgColor = design?.colors?.background || '#FFFFFF';
  const textColor = design?.colors?.text || '#1E1E1E';
  const textLight = design?.colors?.textLight || '#6B6B6B';
  const borderColor = design?.colors?.border || '#E8E4DA';
  const headingFont = design?.typography?.headingFont || "'Inter', sans-serif";
  const bodyFont = design?.typography?.bodyFont || "'Inter', sans-serif";
  const containerMaxWidth = design?.spacing?.containerMaxWidth || '1200px';
  const sectionPaddingTop = design?.spacing?.sectionPaddingTop || '60px';
  const sectionPaddingBottom = design?.spacing?.sectionPaddingBottom || '60px';

  // Merge with defaults only for missing top-level keys
  const mergedHours = { ...defaultHours, ...hours };

  // Helper to render a single day entry (or nested object)
  const renderEntry = (key: string, value: any, level = 0) => {
    const label = dayLabels[key] || key.charAt(0).toUpperCase() + key.slice(1);
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Nested object: render as a group
      return (
        <div key={key} style={{ marginBottom: '0.5rem' }}>
          <div style={{ fontWeight: 600, color: textColor, fontSize: '0.9rem', marginBottom: '0.25rem' }}>
            {label}
          </div>
          <div style={{ paddingLeft: '1rem' }}>
            {Object.entries(value).map(([subKey, subVal]) => renderEntry(subKey, subVal, level + 1))}
          </div>
        </div>
      );
    } else {
      // Leaf value: display as a row
      return (
        <div
          key={key}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0.5rem 0',
            borderBottom: `1px solid ${borderColor}`,
            fontFamily: bodyFont,
            fontSize: '0.9rem',
          }}
        >
          <span style={{ color: textLight }}>{label}</span>
          <span style={{ color: textColor, fontWeight: 500 }}>{String(value)}</span>
        </div>
      );
    }
  };

  return (
    <section
      className="opening-hours-section"
      style={{
        background: bgColor,
        padding: `${sectionPaddingTop} 0 ${sectionPaddingBottom} 0`,
        ...style,
      }}
    >
      <div className="container" style={{ maxWidth: containerMaxWidth, margin: '0 auto', padding: '0 1.5rem' }}>
        <h2
          className="opening-hours-title"
          style={{
            fontFamily: headingFont,
            fontSize: '1.8rem',
            fontWeight: 600,
            color: textColor,
            marginBottom: '1.5rem',
            textAlign: 'center',
          }}
        >
          {title}
        </h2>
        <div className="opening-hours-grid">
          {Object.entries(mergedHours).map(([key, value]) => renderEntry(key, value))}
        </div>
      </div>
    </section>
  );
}